import React from 'react'
import { Select, Input, Button, Checkbox, Typography, Divider, Space, Tag, Empty, Table, Form } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
const { Title, Text, Paragraph } = Typography
const { Option } = Select

const INTRO_QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
}
const INTRO_QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link']

const isIntroEmpty = (html) => {
  if (!html) return true
  const stripped = html.replace(/<(?:.|\n)*?>/g, '').replace(/&nbsp;/g, '').trim()
  return stripped.length === 0
}

export const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'number', label: 'Number' },
  { value: 'phone', label: 'Phone (8 digits)' },
  { value: 'vpsAccount', label: 'VPS account (12 digits)' },
  { value: 'checkbox', label: 'Checkbox (Yes/No)' },
  { value: 'dropdown', label: 'Dropdown' }
]

const PdfFieldList = ({ pdfFieldNames = [] }) => {
  if (!pdfFieldNames.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No AcroForm fields were detected in the PDF"
      />
    )
  }
  return (
    <div>
      <Paragraph style={{ marginBottom: 8 }}>
        <Text type="secondary">
          {pdfFieldNames.length} field{pdfFieldNames.length === 1 ? '' : 's'} detected in the PDF. Map each one you want the customer to fill ahead of signing:
        </Text>
      </Paragraph>
      <div style={{ marginBottom: 8 }}>
        {pdfFieldNames.map(n => <Tag key={n} style={{ marginBottom: 4 }}>{n}</Tag>)}
      </div>
    </div>
  )
}

const MappingEditor = ({
  pdfFieldNames = [],
  fields = [],
  formTitle = '',
  formIntro = '',
  onChange
}) => {
  const usedPdfFields = new Set(fields.filter(f => f.pdfField).map(f => f.pdfField))

  const emit = (patch) => {
    onChange({ fields, formTitle, formIntro, ...patch })
  }

  const updateField = (idx, patch) => {
    const next = fields.map((f, i) => i === idx ? { ...f, ...patch } : f)
    emit({ fields: next })
  }

  const addField = () => {
    emit({
      fields: [
        ...fields,
        { key: '', label: '', type: 'text', required: false, pdfField: null }
      ]
    })
  }

  const removeField = (idx) => {
    emit({ fields: fields.filter((_, i) => i !== idx) })
  }

  const availablePdfFieldsFor = (currentValue) =>
    pdfFieldNames.filter(n => !usedPdfFields.has(n) || n === currentValue)

  const columns = [
    {
      title: 'Key',
      dataIndex: 'key',
      width: 180,
      render: (_, row, idx) => (
        <Input
          placeholder="companyName"
          value={row.key}
          onChange={(e) => updateField(idx, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
        />
      )
    },
    {
      title: 'Label (shown to customer)',
      dataIndex: 'label',
      render: (_, row, idx) => (
        <Input
          placeholder="Company name"
          value={row.label}
          onChange={(e) => updateField(idx, { label: e.target.value })}
        />
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 180,
      render: (_, row, idx) => (
        <Select
          value={row.type}
          onChange={(v) => updateField(idx, { type: v })}
          style={{ width: '100%' }}
        >
          {FIELD_TYPES.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
        </Select>
      )
    },
    {
      title: 'Options',
      dataIndex: 'options',
      width: 220,
      render: (_, row, idx) => row.type === 'dropdown' ? (
        <Select
          mode="tags"
          placeholder="Add options…"
          value={row.options || []}
          onChange={(vals) => updateField(idx, { options: vals })}
          style={{ width: '100%' }}
          tokenSeparators={[',']}
        />
      ) : (
        <Text type="secondary">—</Text>
      )
    },
    {
      title: 'Required',
      dataIndex: 'required',
      width: 90,
      align: 'center',
      render: (_, row, idx) => (
        <Checkbox
          checked={!!row.required}
          onChange={(e) => updateField(idx, { required: e.target.checked })}
        />
      )
    },
    {
      title: 'Maps to PDF field',
      dataIndex: 'pdfField',
      width: 240,
      render: (_, row, idx) => (
        <Select
          allowClear
          placeholder="— not in PDF —"
          value={row.pdfField || undefined}
          onChange={(v) => updateField(idx, { pdfField: v || null })}
          style={{ width: '100%' }}
          disabled={pdfFieldNames.length === 0}
        >
          {availablePdfFieldsFor(row.pdfField).map(n => <Option key={n} value={n}>{n}</Option>)}
        </Select>
      )
    },
    {
      title: '',
      dataIndex: '__remove',
      width: 50,
      align: 'center',
      render: (_, row, idx) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeField(idx)}
        />
      )
    }
  ]

  return (
    <div>
      <Title level={5}>Form header</Title>
      <Text type="secondary">
        Shown to the customer at the top of the signing form. Leave blank to fall back to the document name.
      </Text>
      <Form layout="vertical" style={{ marginTop: 12 }}>
        <Form.Item label="Title">
          <Input
            placeholder="e.g. Project Kona – Acceptance form"
            value={formTitle}
            maxLength={200}
            onChange={(e) => emit({ formTitle: e.target.value })}
          />
        </Form.Item>
        <Form.Item label="Intro text" help="Supports headings, bold/italic, bullet and numbered lists, and links.">
          <div className="introEditor">
            <ReactQuill
              theme="snow"
              value={formIntro}
              onChange={(html) => emit({ formIntro: isIntroEmpty(html) ? '' : html })}
              modules={INTRO_QUILL_MODULES}
              formats={INTRO_QUILL_FORMATS}
              placeholder="Please confirm the details below before signing…"
            />
          </div>
        </Form.Item>
      </Form>

      <Divider />

      <Title level={5}>Detected PDF fields</Title>
      <PdfFieldList pdfFieldNames={pdfFieldNames} />

      <Divider />

      <Space align="center" style={{ justifyContent: 'space-between', width: '100%', marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>Form fields</Title>
        <Button icon={<PlusOutlined />} onClick={addField} size="small">Add field</Button>
      </Space>
      <Text type="secondary">
        Each field becomes an input on the customer's form. Values for fields with a PDF mapping are written into the PDF (and still editable in Signicat). Values for unmapped fields are stored on the database entry only and appear in the Excel export.
      </Text>

      <Table
        dataSource={fields.map((f, idx) => ({ ...f, __key: idx }))}
        columns={columns}
        rowKey="__key"
        pagination={false}
        size="small"
        style={{ marginTop: 12 }}
        locale={{ emptyText: 'No fields — add at least one or the customer will skip straight to Signicat.' }}
      />
    </div>
  )
}

export default MappingEditor
